import ScopesSelect from 'apps/common/Scopes/ScopesSelect';
import Button from 'common/components/Button';
import Description from 'common/components/Description';
import ErrorComponent from 'common/components/Error';
import ImageUpload from 'common/components/ImageUpload';
import LinkOut from 'common/components/LinkOut';
import MultiInput from 'common/components/MultiInput';
import config from 'config';
import React, { useEffect, useState } from 'react';
import { FieldValues, useForm, useFieldArray, FormProvider, Control } from 'react-hook-form';
import { IconButton } from '@whoop/web-components';
import styles from './index.module.scss';

export interface AppFormValues {
  contacts: { value: string }[];
  redirectUrls: { value: string }[];
  name: string;
  privacyPolicyUrl: string;
  scopes: Record<string, boolean>;
  logoUrl?: string;
  // Webhook id is only ever populated in an edit/update scenario
  webhookId?: string;
  webhooks: { url: string; model_version: string; id?: string }[];
}

interface AppFormProps {
  availableScopes: string[];
  defaultValues: AppFormValues;
  cta: string;
  onSubmit: (v: AppFormValues) => void;
  loading?: boolean;
  error?: string;
  webhookError?: boolean;
}

interface FormErrorDetail {
  fieldName: string;
  message: string;
  label?: string;
}

function AppForm({
  availableScopes,
  defaultValues,
  cta,
  loading = false,
  error,
  onSubmit,
  webhookError = false,
}: AppFormProps) {
  // Restore useForm from 'react-hook-form'
  const methods = useForm<AppFormValues>({
    defaultValues: {
      ...defaultValues,
      webhooks: defaultValues.webhooks || [], // Ensure webhooks is initialized as an array
    } as FieldValues, // Cast to FieldValues for defaultValues
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // Subscribe to specific fields for validation. methods.watch() without arguments already triggers re-render when any value changes; avoid storing the reference.
  methods.watch();

  // useFieldArray from react-hook-form, uses control from react-hook-form's useForm
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'webhooks',
  });

  const noScopesSelected = !Object.values(methods.watch('scopes') || {}).find(
    (x) => x,
  );

  const handleSubmitForm = (data: AppFormValues) => {
    onSubmit(data);
  };

  const [submitDisabledMessage, setSubmitDisabledMessage] = useState<string | null>(null);
  const [detailedErrors, setDetailedErrors] = useState<FormErrorDetail[]>([]);

  // Watch individual required fields so that the summary updates as soon as the user types
  const nameWatch = methods.watch('name');
  const privacyWatch = methods.watch('privacyPolicyUrl');
  const contactsWatch = methods.watch('contacts');
  const redirectsWatch = methods.watch('redirectUrls');

  const getFieldLabel = (rhfPath: string): string => {
    if (rhfPath === 'name') return 'App Name';
    if (rhfPath === 'privacyPolicyUrl') return 'Privacy Policy URL';

    let match = rhfPath.match(/^contacts\.(\d+)\.value$/);
    if (match) return `Contact #${parseInt(match[1], 10) + 1}`;

    match = rhfPath.match(/^redirectUrls\.(\d+)\.value$/);
    if (match) return `Redirect URL #${parseInt(match[1], 10) + 1}`;

    match = rhfPath.match(/^webhooks\.(\d+)\.url$/);
    if (match) return `Webhook #${parseInt(match[1], 10) + 1} URL`;

    match = rhfPath.match(/^webhooks\.(\d+)\.model_version$/);
    if (match) return `Webhook #${parseInt(match[1], 10) + 1} Model Version`;
    
    const parts = rhfPath.split('.');
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  };

  useEffect(() => {
    if (loading) {
      setSubmitDisabledMessage('Processing...');
      setDetailedErrors([]);
    } else if (!methods.formState.isDirty) {
      setSubmitDisabledMessage('Make a change to enable saving.');
      setDetailedErrors([]);
    } else if (noScopesSelected) {
      setSubmitDisabledMessage('Please select at least one scope.');
      setDetailedErrors([]);
    } else if (!methods.formState.isValid) {
      setSubmitDisabledMessage('Please correct the following errors in the form:');
      const currentErrors: FormErrorDetail[] = [];

      const extractErrors = (errorObject: any, pathPrefix: string = '') => {
        Object.keys(errorObject).forEach(key => {
          const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;
          const errorField = errorObject[key];

          if (errorField) {
            if (errorField.message && typeof errorField.message === 'string') {
              currentErrors.push({
                fieldName: currentPath,
                message: errorField.message,
                label: getFieldLabel(currentPath),
              });
            } else if (typeof errorField === 'object' && !errorField.message && !(errorField instanceof React.Component) && typeof errorField.type === 'undefined') {
              extractErrors(errorField, currentPath);
            }
          }
        });
      };

      extractErrors(methods.formState.errors);

      // fallback presence check same as before
      if (currentErrors.length === 0) {
        const requiredMissing: { path: string; label: string; message: string }[] = [];
        const nameVal = nameWatch;
        if (!nameVal || nameVal.trim() === '') {
          requiredMissing.push({ path: 'name', label: 'App Name', message: 'is required' });
        }
        const policyVal = privacyWatch;
        if (!policyVal || policyVal.trim() === '') {
          requiredMissing.push({ path: 'privacyPolicyUrl', label: 'Privacy Policy URL', message: 'is required' });
        }
        const contactsVal = contactsWatch;
        if (!contactsVal || contactsVal.some((c: { value: string }) => !c.value || c.value.trim() === '')) {
          requiredMissing.push({ path: 'contacts.0.value', label: 'Contact', message: 'is required' });
        }
        const redirectsVal = redirectsWatch;
        if (!redirectsVal || redirectsVal.some((r: { value: string }) => !r.value || r.value.trim() === '')) {
          requiredMissing.push({ path: 'redirectUrls.0.value', label: 'Redirect URL', message: 'is required' });
        }
        requiredMissing.forEach((req) => currentErrors.push({ fieldName: req.path, label: req.label, message: req.message }));
      }

      setDetailedErrors(currentErrors);
    } else {
      setSubmitDisabledMessage(null);
      setDetailedErrors([]);
    }
  }, [loading, methods.formState.isDirty, methods.formState.isValid, noScopesSelected, methods.formState.errors, nameWatch, privacyWatch, contactsWatch, redirectsWatch]);

  const handleErrorClick = (rhfPath: string) => {
    let elementId = rhfPath;
    // Use const for match as it's not reassigned
    const matchArray = rhfPath.match(/^(contacts|redirectUrls)\.(\d+)\.value$/);
    if (matchArray) {
      elementId = `${matchArray[1]}-${matchArray[2]}`; 
    }
    // For webhooks (e.g., webhooks.0.url) and simple fields (e.g., name), the rhfPath often matches the id.

    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        if (typeof element.select === 'function') {
          try {
            element.select();
          } catch (e) {
            // console.warn("Failed to select text for element:", elementId, e);
          }
        }
      }
    } else {
      // console.warn(`Element with ID '${elementId}' not found for RHF path '${rhfPath}'`);
    }
  };

  return (
    <div className={styles.root}>
      {/* Restore FormProvider and native form from react-hook-form */}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmitForm)}>
          <Description label="Name">
            <p>App name displayed to the user in the OAuth flow.</p>
            <input
              type="text"
              id="name"
              className={`${styles.formInput} ${methods.formState.errors?.name ? styles.inputError : ''}`.trim()}
              {...methods.register("name", { required: { value: true, message: 'Name is required' } })}
              data-testid="name-input"
              placeholder="App Name"
            />
            {methods.formState.errors?.name && (
              <p className={styles.errorMessage} data-testid="name-error">
                {methods.formState.errors?.name?.message}
              </p>
            )}
          </Description>

          <Description label="Logo">
            <p>
              Logo displayed to the user in the OAuth flow. This logo must be
              either .jpg or .png and will be displayed in 1:1 width/height pixel
              ratio.
            </p>
            <ImageUpload
              setValue={methods.setValue}
              defaultValue={defaultValues.logoUrl}
              name="logoUrl"
            />
          </Description>

          <Description label="Contacts">
            <p>
              Contact emails for this app. Administrative communications from
              WHOOP concerning this app will be directed to these emails.
            </p>
            <MultiInput
              control={methods.control as unknown as Control<FieldValues, any>}
              name="contacts"
              min={1}
              label={(i) => `Contact #${i + 1}`}
              type="text"
              required={{ value: true, message: 'This field is required' }}
              pattern={{ value: /\S+@\S+\.\S+/, message: 'Invalid email' }}
              data-testid="contacts-multi-input"
              placeholder="contact@example.com"
            />
          </Description>

          <Description label="Privacy Policy">
            <p>
              Link to your privacy policy. Users will be presented with this link
              in the OAuth flow.
            </p>
            <input
              type="text"
              id="privacyPolicyUrl"
              className={`${styles.formInput} ${methods.formState.errors?.privacyPolicyUrl ? styles.inputError : ''}`.trim()}
              {...methods.register("privacyPolicyUrl", {
                required: { value: true, message: 'Privacy Policy URL is required' },
                pattern: {
                  value: /^https?:\/\/\S+/,
                  message: 'Please enter a valid URL'
                }
              })}
              data-testid="privacy-policy-url-input"
              placeholder="https://example.com/privacy"
            />
            {methods.formState.errors?.privacyPolicyUrl && (
              <p className={styles.errorMessage} data-testid="privacy-policy-url-error">
                {methods.formState.errors?.privacyPolicyUrl?.message}
              </p>
            )}
          </Description>

          <Description label="Redirect URLs">
            <p>
              Location to which the user is taken after logging in. A valid
              redirect URL will take the form of https://whoop.com or
              whoop://example.
              <br />
              <br />
              <LinkOut href={config.docs.oauthRedirects}>Learn more</LinkOut>{' '}
              about OAuth redirects.
            </p>
            <MultiInput
              control={methods.control as unknown as Control<FieldValues, any>}
              name="redirectUrls"
              min={1}
              label={(i) => `Redirect #${i + 1}`}
              type="text"
              required={{ value: true, message: 'This field is required' }}
              pattern={{
                value: /\S+:\/\/[^#\s]+$/,
                message: 'Redirect URL must not contain "#" fragments',
              }}
              data-testid="redirect-urls-multi-input"
              placeholder="https://myapp.com/callback"
            />
          </Description>

          <Description label="Scopes">
            <p>
              Scopes determine what data your app can access on a user&apos;s
              behalf.
              <br />
              <br />
              <LinkOut href={config.docs.oauthScopes}>Learn more</LinkOut> about
              OAuth scopes.
            </p>
            <ScopesSelect
              availableScopes={availableScopes}
              defaultValues={defaultValues?.scopes}
              onSelect={(scope, checked) => {
                methods.setValue(`scopes.${scope}`, checked, { shouldDirty: true });
              }}
            />
          </Description>

          <Description label="Webhooks">
            <p>
              Webhook URLs to receive updates from WHOOP and their respective API model versions.
              WHOOP will send updates to these URLs when changes occur to member data.
              Each URL must start with &quot;https&quot;.
              <br />
              <br />
              <LinkOut href={config.docs.webhooks}>Learn more</LinkOut> about
              Webhooks.
            </p>
            {webhookError && (
              <ErrorComponent>
                We couldn&apos;t create your webhook. Please correct the details below and save the app.
              </ErrorComponent>
            )}
            <div>
              {fields.map((field, index) => (
                <div key={field.id} className={styles.webhookEntry}>
                  <div className={styles.webhookUrlInputContainer}>
                    {(() => {
                      const urlFieldPath = `webhooks.${index}.url` as const;
                      const urlValue = methods.getValues(urlFieldPath);
                      const urlError = methods.formState.errors.webhooks?.[index]?.url;
                      const urlHighlight = !!urlError || (!urlValue && !methods.formState.isValid);
                      return (
                        <label className={styles.selectLabel} htmlFor={urlFieldPath}>
                          Webhook URL
                          <input
                            type="text"
                            id={urlFieldPath}
                            className={`${styles.formInput} ${urlHighlight ? styles.inputError : ''}`.trim()}
                            {...methods.register(urlFieldPath, {
                              required: 'Webhook URL is required',
                              pattern: {
                                value: /^https:\/\/\S+/,
                                message: 'Webhook URL must start with "https" and be a valid URL',
                              },
                            })}
                            data-testid={`webhook-url-${index}`}
                            placeholder="https://example.com/webhook"
                          />
                        </label>
                      );
                    })()}
                    {methods.formState.errors.webhooks?.[index]?.url && (
                      <p className={styles.errorMessage} data-testid={`webhook-url-${index}-error`}>
                        {methods.formState.errors.webhooks[index]?.url?.message}
                      </p>
                    )}
                  </div>

                  <div className={styles.webhookVersionSelectContainer}>
                    <label className={styles.selectLabel} htmlFor={`webhooks.${index}.model_version`}>
                      Model Version
                      <select
                        id={`webhooks.${index}.model_version`}
                        {...methods.register(`webhooks.${index}.model_version` as const, {
                          required: 'Model version is required',
                        })}
                        className={`${styles.selectInput} ${methods.formState.errors.webhooks?.[index]?.model_version ? styles.inputError : ''}`.trim()}
                        data-testid={`webhook-version-${index}`}
                      >
                        <option value="v2">V2</option>
                      </select>
                    </label>
                    {methods.formState.errors.webhooks?.[index]?.model_version && (
                      <p className={styles.errorMessage} data-testid={`webhook-version-${index}-error`}>
                        {methods.formState.errors.webhooks[index]?.model_version?.message}
                      </p>
                    )}
                  </div>

                  <div className={styles.webhookDeleteButtonContainer}>
                    <IconButton
                      name="trash"
                      onClick={() => remove(index)}
                      theme="dark"
                      aria-label="Delete Webhook"
                      data-testid={`remove-webhook-${index}`}
                      className={styles.webhookDeleteIconButton}
                    />
                  </div>
                </div>
              ))}
              <Button
                type="button"
                onClick={() => append({ id: undefined, url: '', model_version: 'v2' })}
                theme="dark"
                variant="secondary"
                data-testid="add-webhook-button"
                className={styles.addButton}
              >
                Add
              </Button>
            </div>
          </Description>

          <br />
          {error && (
            <div data-testid="form-api-error-container" className={styles.apiErrorContainer}>
              <ErrorComponent>{error}</ErrorComponent>
              <p className={styles.apiErrorSupportMessage}>
                Reach out to <a 
                  href={config.appApprovalSubmissionUrl.includes('@') && !config.appApprovalSubmissionUrl.startsWith('mailto:') ? `mailto:${config.appApprovalSubmissionUrl}` : config.appApprovalSubmissionUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.supportLink}
                >
                  support
                </a> if it continues.
              </p>
            </div>
          )}
          <br />
          <Button
            type="submit"
            variant="primary"
            theme="dark"
            disabled={loading || !methods.formState.isDirty || !methods.formState.isValid || noScopesSelected}
            data-testid="submit-button"
          >
            {cta}
          </Button>
          {submitDisabledMessage && !detailedErrors.length && (
            <p className={styles.disabledButtonMessage} data-testid="submit-disabled-message">
              {submitDisabledMessage}
            </p>
          )}
          {submitDisabledMessage && detailedErrors.length > 0 && (
             <div className={styles.errorSummaryContainer}>
                <p className={styles.disabledButtonMessage} data-testid="submit-disabled-message">
                    {submitDisabledMessage}
                </p>
                <ul className={styles.errorList} data-testid="form-error-list">
                    {detailedErrors.map((err) => (
                    <li key={err.fieldName} className={styles.errorListItem}>
                        <button
                        type="button"
                        onClick={() => handleErrorClick(err.fieldName)}
                        className={styles.errorLink}
                        data-testid={`error-link-${err.fieldName.replace(/\./g, '-')}`}
                        >
                        {err.label || err.fieldName}
                        </button>
                        : {err.message}
                    </li>
                    ))}
                </ul>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
}

export default AppForm;